import { useState, useEffect } from "react";
import { fetchWithToken } from "../../../hooks/useFetch";
import { useAuth } from "src/contexts/auth";
import { BusyOverlay } from "src/components/BusyOverlay";

type InstrumentReportProps = {
  tenantID: string;
  instrumentID: string;
  instrumentSerialNumber: string;
  instrumentName: string;
  runID: string;
};

type InstrumentReportResponse = {
  exists: boolean;
  s3Key: string;
  reportURL: string;
  message: string;
};

const RunInstrumentReport = ({
  tenantID,
  instrumentID,
  instrumentSerialNumber,
  instrumentName,
  runID,
}: InstrumentReportProps) => {
  const { getAccessTokenSilently } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchReport = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      setReportUrl(null);

      try {
        const response: InstrumentReportResponse = await fetchWithToken(
          getAccessTokenSilently,
          "/run/instrumentReport",
          {
            method: "POST",
            body: JSON.stringify({
              RunID: runID,
              InstrumentID: instrumentID,
              InstrumentName: instrumentName,
              InstrumentSerialNumber: instrumentSerialNumber,
              TenantID: tenantID,
            }),
          }
        );

        if (response.exists && response.reportURL) {
          setReportUrl(response.reportURL);
        } else {
          setErrorMessage(response.message || "Instrument report not available");
        }
      } catch (err: any) {
        // The error message might be JSON from a 404 response
        try {
          const errorResponse = JSON.parse(err.message);
          setErrorMessage(errorResponse.message || "Instrument report not available");
        } catch {
          // Not JSON, use the raw message or default
          setErrorMessage(err?.message || "Instrument report has not been generated yet");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [
    runID,
    instrumentID,
    instrumentName,
    instrumentSerialNumber,
    tenantID,
    getAccessTokenSilently,
  ]);

  if (isLoading) {
    return (
      <div style={{ position: "relative", height: "80vh" }}>
        <BusyOverlay />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "50vh",
          color: "#666",
        }}
      >
        <div
          style={{
            fontSize: "48px",
            marginBottom: "16px",
          }}
        >
          📄
        </div>
        <div
          style={{
            fontSize: "18px",
            fontWeight: 500,
            marginBottom: "8px",
          }}
        >
          Report Not Available
        </div>
        <div
          style={{
            fontSize: "14px",
            color: "#999",
          }}
        >
          {errorMessage}
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "80vh" }}>
      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <a
          href={reportUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: "8px 16px",
            backgroundColor: "#1976d2",
            color: "white",
            textDecoration: "none",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          Open in New Tab ↗
        </a>
      </div>
      <iframe
        src={reportUrl || ""}
        title="Instrument Report"
        style={{
          width: "100%",
          height: "calc(100% - 48px)",
          border: "1px solid #ddd",
          borderRadius: "4px",
        }}
      />
    </div>
  );
};

export default RunInstrumentReport;

